import {
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  addDoc,
  collection,
  collectionData,
  CollectionReference,
  doc,
  Firestore,
  query,
  Timestamp,
  updateDoc,
  where,
  orderBy,
} from '@angular/fire/firestore';
import {
  Payout,
  payoutConverter,
  PayoutStatus,
} from 'src/app/models/payout.model';

@Injectable({
  providedIn: 'root',
})
export class PayoutsStoreService {
  private _firestore = inject(Firestore);
  private _payoutsCollectionRef: CollectionReference<Payout>;
  private _payouts: WritableSignal<Payout[]> = signal<Payout[]>([]);
  payouts: Signal<Payout[] | undefined> = this._payouts.asReadonly();

  constructor() {
    this._payoutsCollectionRef = collection(
      this._firestore,
      'payouts',
    ).withConverter(payoutConverter);
  }

  getUserPayouts(userId: string) {
    if (!userId) {
      return;
    }
    const q = query(
      this._payoutsCollectionRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
    );

    collectionData(q, { idField: 'id' }).subscribe((payouts) => {
      this._payouts.set(payouts);
    });
  }

  getAllPayouts() {
    const q = query(this._payoutsCollectionRef, orderBy('createdAt', 'desc'));

    collectionData(q, { idField: 'id' }).subscribe((payouts) => {
      this._payouts.set(payouts);
    });
  }

  async createPayout(
    userId: string,
    userEmail: string,
    paymentName: string,
    paymentEmail: string,
    amount: number,
  ): Promise<void> {
    const payout: Payout = {
      userId,
      userEmail,
      paymentName,
      paymentEmail,
      amount,
      status: PayoutStatus.REQUESTED,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await addDoc(this._payoutsCollectionRef, payout);
  }

  async updatePayoutStatus(
    payoutId: string,
    status: PayoutStatus,
  ): Promise<void> {
    const payoutDoc = doc(this._firestore, 'payouts', payoutId);
    await updateDoc(payoutDoc, {
      status,
      updatedAt: Timestamp.now(),
    });
  }
}
